import { getSupportedCountryCurrency } from '@change-corgi/config/currency';
import { GQLResponseError, GQLResponseServerError } from '@change-corgi/core/gql';
import type { UtilityContext } from '@change-corgi/core-react/utilityContext';

import { getSubscriptionConfig } from '../../api';

import type { MembershipBottomBannerDataQuery, MembershipBottomBannerDataQueryVariables } from './api.graphql';
import { MembershipBottomBannerData as MembershipBottomBannerDataQ } from './api.graphql';

type SubscriptionConfig =
	| Pick<NonNullable<Awaited<ReturnType<typeof getSubscriptionConfig>>>, 'amounts' | 'defaultAmount'>
	| undefined;

export type MembershipBottomBannerData = {
	subscriptionConfig?: SubscriptionConfig;
	featureConfigs: MembershipBottomBannerDataQuery['featureConfigs'];
	currency: string;
};

export async function getMembershipBottomBannerData(
	utilityContext: UtilityContext,
	{
		countryCode,
	}: {
		countryCode: string;
	},
): Promise<MembershipBottomBannerData> {
	const currency = getSupportedCountryCurrency(countryCode) || 'USD';

	const [{ data }, subscriptionConfig] = await Promise.all([
		utilityContext.gql.fetch<MembershipBottomBannerDataQuery, MembershipBottomBannerDataQueryVariables>({
			query: MembershipBottomBannerDataQ,
			rejectOnError: true,
		}),
		// a GraphQL response error (body-level errors) here shouldn't take down the whole banner:
		// subscriptionConfig degrades to undefined instead of rejecting the Promise.all
		// (see getSubscriptionConfig's own rejectOnError: true comment).
		// Server/network-level failures (GQLResponseServerError, incl. PerimeterX challenges) and any
		// other unexpected error are rethrown so they aren't silently hidden.
		getSubscriptionConfig(utilityContext, currency).catch((error) => {
			if (error instanceof GQLResponseError && !(error instanceof GQLResponseServerError)) {
				return undefined;
			}
			throw error;
		}),
	]);

	return {
		currency,
		subscriptionConfig: subscriptionConfig
			? { amounts: subscriptionConfig.amounts, defaultAmount: subscriptionConfig.defaultAmount }
			: undefined,
		featureConfigs: data.featureConfigs,
	};
}
